/**
 * Project the live A/P canvas state onto the note rail's grouped A/P view.
 *
 * The design (`EncounterScreen.dc.html` lines 820-846, data at 956-976) shows the right-rail note,
 * on the A/P step, as `Diagnosis` followed by one group per order type — each row a code gutter, a
 * name, and an optional secondary line.
 *
 * THE CODE GUTTER IS THE DIAGNOSIS CODE, NOT THE ORDER'S OWN CODE. In the design every order row
 * repeats `S32.464K`, the primary diagnosis it justifies; the order's own CPT/product code is the
 * `sub` line underneath. That reads as "this is what we did, and here is the problem it answers" —
 * the clinical justification is the thing the gutter aligns on. Getting this backwards would make
 * the note look right and say something different.
 *
 * MIRROR, NOT A COPY: this derives from the same `DxCard[]` / `PlanOrder[]` the canvas renders, so
 * the rail cannot drift from the canvas. Nothing here fetches or caches.
 */
import type { DxCard, OrderType, PlanOrder } from '@scripsteam/scrips-signal-ds';
import type { ApNoteSection } from '../encounter-drawer';

/**
 * Section heading per order type. The four the design names use its exact wording; `imaging` and
 * `referral` are not drawn there but ARE real order types, and an order the clinician placed must
 * never be silently missing from the note it generates.
 */
const ORDER_SECTION_TITLE: Record<OrderType, string> = {
  lab: 'Labs',
  medication: 'Medications',
  vaccination: 'Vaccination',
  imaging: 'Imaging',
  procedure: 'Procedure',
  referral: 'Referral',
};

/**
 * Section order. Follows the design for the four it shows (Labs → Medications → Vaccination →
 * Procedure) and slots the two it omits in where they read naturally.
 */
const ORDER_SECTION_SEQUENCE: OrderType[] = [
  'lab',
  'medication',
  'vaccination',
  'imaging',
  'procedure',
  'referral',
];

export interface ApNoteInput {
  diagnoses: DxCard[];
  orders: PlanOrder[];
  /**
   * The order's own secondary line — its CPT/product code, or a medication sig. OPTIONAL because
   * `PlanOrder` does not carry it: the canvas type is `{ id, type, label, dxIds }`, and the richer
   * detail lives in the host's order payloads. Return undefined and the row renders name-only
   * rather than a fabricated code.
   */
  orderDetail?: (order: PlanOrder) => string | undefined;
  /**
   * The A/P canvas's free-text assessment note. Persisted in the CreateAssessment body but,
   * until 2026-09-09, never rendered in the note — Flutter prints it as a leading `Notes` row
   * (`assesment_plan_note_component_widget.dart:83-108`). Blank/whitespace → no row.
   */
  assessmentNote?: string | null;
}

/**
 * Build the rail's A/P sections. Returns `[]` when there is nothing placed, which the drawer treats
 * as "fall back to the derived questionnaire note" — an empty A/P must not blank the rail.
 */
export function toApNoteSections({
  diagnoses,
  orders,
  orderDetail,
  assessmentNote,
}: ApNoteInput): ApNoteSection[] {
  const sections: ApNoteSection[] = [];

  // Flutter draws this ABOVE `diagnosisWidget` (`:109`), so it leads the list.
  const note = (assessmentNote ?? '').trim();
  if (note) sections.push({ title: 'Notes', kind: 'notes', rows: [{ name: note }] });
  

  if (diagnoses.length > 0) {
    sections.push({
      title: 'Diagnosis',
      kind: 'diagnosis',
      rows: diagnoses.map((dx) => ({ code: dx.code, name: dx.display })),
    });
  }

  // The code an order row shows in its gutter: the diagnosis it justifies. An order links to 1..N
  // diagnoses (`dxIds`); the FIRST link is its primary reason, matching how the canvas rows and the
  // persisted contract both treat it.
  const dxCodeById = new Map(diagnoses.map((dx) => [dx.id, dx.code]));
  const justifyingCode = (order: PlanOrder): string | undefined => {
    for (const dxId of order.dxIds) {
      const code = dxCodeById.get(dxId);
      if (code) return code;
    }
    return undefined;
  };

  for (const type of ORDER_SECTION_SEQUENCE) {
    const rows = orders
      .filter((order) => order.type === type)
      .map((order) => ({
        code: justifyingCode(order),
        name: order.label,
        sub: orderDetail?.(order),
      }));
    if (rows.length > 0) sections.push({ title: ORDER_SECTION_TITLE[type], kind: 'order', rows });
  }

  return sections;
}
